<template>
  <b-card>
    <b-row>
      <b-col sm="12">
        <h4>{{i18nT(`Document types`)}}</h4>
        <p>{{ i18nT(`Configure your document types. Use drag and drop to change the order.`) }}</p>
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          class="mb-1 mr-1 pl-1"
          v-b-modal.modal-document-type
          @click="resetForm"
        >
          <feather-icon
            icon="PlusCircleIcon"
            class="mr-1"
          />
          {{i18nT(`Add new`)}}
        </b-button>
      </b-col>
    </b-row>
    <draggable
      v-model="documentTypes"
      tag="ul"
      class="list-group cursor-move"
      @end="updateOrder"
    >
      <b-list-group-item
        v-for="(documentType, index) in documentTypes"
        :key="index"
        tag="li"
      >
        <div class="draggable-container">
          <div>
            <strong> {{ documentType.Label }}</strong>
          </div>
          <div class="d-flex align-items-center">
            <feather-icon
              icon="Edit2Icon"
              size="17"
              class="cursor-pointer d-sm-block d-none mr-1"
              @click="onEdit(documentType)"
            />
            <feather-icon
              icon="Trash2Icon"
              size="17"
              class="cursor-pointer d-sm-block d-none mr-1"
              @click="onDelete(documentType)"
            />
          </div>
        </div>
      </b-list-group-item>
    </draggable>

    <!-- MODAL -->
    <b-modal
      id="modal-document-type"
      :title="i18nT(`Document type`)"
      ok-only
      :ok-title="i18nT(`Save`)"
      no-close-on-backdrop
      @ok="validationForm"
    >
      <span>{{ i18nT(`Create document types (e.g. Access information, Benefits, Contract agreements...).`) }}</span>
      <validation-observer ref="simpleRules">
        <b-form class="mt-2">
          <b-row>

            <b-col sm="12">
              <b-form-group
                :label="i18nT(`Document type`)"
                label-for="typeName"
                class="required"
              >
                <validation-provider
                  #default="{ errors }"
                  name="typeName"
                  rules="required"
                >
                  <b-form-input
                    v-model="typeName"
                    :placeholder="i18nT(`E.g. Contract agreement`)"
                    name="subject"
                    :state="errors.length > 0 ? false:null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BCard,
  BListGroupItem
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import draggable from 'vuedraggable'
import _ from 'lodash'

export default {
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BCard,
    ValidationProvider,
    ValidationObserver,
    BListGroupItem,
    draggable
  },
  directives: {
    Ripple
  },
  props: {
    generalData: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      documentTypes: [],
      currentType: {},
      typeName: ''
    }
  },
  methods: {
    resetForm() {
      this.currentType = {}
      this.typeName = ''
    },
    validationForm(event) {
      event.preventDefault()

      this.$refs.simpleRules.validate().then(success => {
        if (success) {
          const formData = new FormData()
          if (!_.isEmpty(this.currentType)) {
            formData.append('id', this.currentType.Id)
          }
          formData.append('Label', this.typeName)

          this.$http
            .post('documents/documentTypes', formData)
            .then(res => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: this.successMessage(res),
                  icon: 'InfoIcon',
                  variant: 'success'
                }
              })

              this.$bvModal.hide('modal-document-type')
              this.loadData()
            })
            .catch(err => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: this.errorMessage(err),
                  icon: 'AlertTriangleIcon',
                  variant: 'danger'
                }
              })
            })
        }
      })
    },
    onEdit(type) {
      this.currentType = type
      this.typeName = type.Label

      this.$bvModal.show('modal-document-type')
    },
    onDelete(type) {
      this.$swal({
        title: this.i18nT(`Are you sure?`),
        text: this.i18nT(`You won't be able to revert this!`),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.i18nT(`Yes, delete it!`),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(({ value }) => {
        if (value) {
          this.$http
            .delete(`documents/documentTypes?id=${type.Id}`)
            .then(res => {
              this.loadData()
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: this.successMessage(res),
                  icon: 'InfoIcon',
                  variant: 'success'
                }
              })
            })
        }
      })
    },
    loadData() {
      this.$http.get(`documents/documentTypes`).then(({ data }) => {
        this.documentTypes = data.data.sort((type1, type2) => {
          return type1.Pos - type2.Pos
        })
      })
    },
    updateOrder() {
      const formData = new FormData()

      formData.append(
        'list',
        this.documentTypes
          .filter(documentType => documentType.Id)
          .map(documentType => documentType.Id)
          .join(',')
      )

      this.$http
        .post('settings/savePositions', formData)
        .then(res => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.successMessage(res),
              icon: 'InfoIcon',
              variant: 'success'
            }
          })

          this.$bvModal.hide('modal-pipeline')
        })
        .catch(err => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.errorMessage(err),
              icon: 'AlertTriangleIcon',
              variant: 'danger'
            }
          })
        })
    }
  },
  setup() {
    return {
      required
    }
  },
  created() {
    this.loadData()
  }
}
</script>

<style lang="scss">
.draggable-container {
  display: flex;
  justify-content: space-between;
}
</style>
